import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Button, Avatar, Chip, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Select, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, IconButton, Tooltip
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const STRATEGY_ENGINE_URL = process.env.REACT_APP_STRATEGY_ENGINE_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev';

function stringToColor(string) {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    color += `00${((hash >> (i * 8)) & 0xff).toString(16)}`.slice(-2);
  }
  return color;
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0]).join('').substring(0, 2).toUpperCase();
}

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planDialog, setPlanDialog] = useState({ open: false, user: null, newPlan: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/admin/users`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setUsers(data.data);
      } else {
        setError(data.error || 'Failed to load users');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleChangePlan = async () => {
    if (!planDialog.user || !planDialog.newPlan) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/admin/users/${planDialog.user.id}/plan`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ planId: planDialog.newPlan }),
      });
      const data = await res.json();
      if (data.success) {
        setPlanDialog({ open: false, user: null, newPlan: '' });
        fetchUsers();
      } else {
        alert(data.error);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    if (!window.confirm(`${newStatus === 'suspended' ? 'Suspend' : 'Activate'} user ${user.email}?`)) return;
    try {
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/admin/users/${user.id}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (data.success) fetchUsers();
      else alert(data.error);
    } catch (err) {
      alert(err.message);
    }
  };

  const planColors = { starter: 'default', professional: 'primary', enterprise: 'secondary' };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Users Management</Typography>
        <Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchUsers} sx={{ mr: 1 }}>
            Refresh
          </Button>
          <Button variant="contained" color="primary" startIcon={<PersonAddIcon />} disabled>
            Add User
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell align="center">Strategies</TableCell>
                <TableCell align="center">Active Runs</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: stringToColor(user.displayName || user.email), mr: 1.5, width: 32, height: 32, fontSize: '0.8rem' }}>
                        {getInitials(user.displayName || user.email)}
                      </Avatar>
                      <Typography variant="body2">{user.displayName || '—'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2">{user.email}</Typography></TableCell>
                  <TableCell>
                    <Chip label={user.role} size="small" color={user.role === 'admin' ? 'warning' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.status} 
                      size="small" 
                      color={user.status === 'active' ? 'success' : user.status === 'suspended' ? 'error' : 'default'} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.planName || user.planId} 
                      size="small" 
                      color={planColors[user.planId] || 'default'}
                      onClick={() => setPlanDialog({ open: true, user, newPlan: user.planId })}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="center">{user.strategyCount}</TableCell>
                  <TableCell align="center">{user.activeRuns}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={user.status === 'active' ? 'Suspend' : 'Activate'}>
                      <IconButton size="small" onClick={() => handleToggleStatus(user)} color={user.status === 'active' ? 'error' : 'success'}>
                        {user.status === 'active' ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Change Plan Dialog */}
      <Dialog open={planDialog.open} onClose={() => setPlanDialog({ open: false, user: null, newPlan: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Change Plan for {planDialog.user?.email}</DialogTitle>
        <DialogContent>
          <Select fullWidth value={planDialog.newPlan} onChange={(e) => setPlanDialog(prev => ({ ...prev, newPlan: e.target.value }))} sx={{ mt: 1 }}>
            <MenuItem value="starter">Starter ($29/mo)</MenuItem>
            <MenuItem value="professional">Professional ($79/mo)</MenuItem>
            <MenuItem value="enterprise">Enterprise ($199/mo)</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog({ open: false, user: null, newPlan: '' })}>Cancel</Button>
          <Button onClick={handleChangePlan} variant="contained" disabled={actionLoading || planDialog.newPlan === planDialog.user?.planId}>
            {actionLoading ? 'Saving...' : 'Change Plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
